// Frontend client for the ccutils/wxcc-function-generator Lambda.
//
// POST /ccutils/wxcc-function — sends the engineer's prompt + optional
// selected-entity data to Bedrock Sonnet 4.6 (via the new Lambda) and
// returns the raw generated JavaScript (ready to paste into the WxCC Code
// editor — no markdown fences, no commentary).
//
// Authentication: the same MSAL Bearer-token flow used by the other CC Utils
// endpoints. The frontend's previous Gemini path used an engineer-supplied
// Google AI Studio key — that UX is gone now: the Lambda's IAM role talks to
// Bedrock and the SPA just passes a normal user JWT.
//
// Endpoint base: REACT_APP_CCUTILS_API_BASE_URL (same as the audio generator
// and UCCX translator). Build pipeline populates this from SSM.

const API_BASE = process.env.REACT_APP_CCUTILS_API_BASE_URL || '';

export function isWxccFunctionBackendConfigured() {
  return !!API_BASE;
}

/**
 * Generate WxCC Flow Designer Function code via Bedrock.
 *
 * @param {object} args
 * @param {string} args.prompt — engineer's free-form description of what the
 *   function should do (the textarea at the bottom of the LLM panel).
 * @param {object} [args.entityData] — optional map of entity-type → array
 *   of { id, name }. Shape matches the FunctionCreator's existing
 *   essentialDataSmall / templateData payloads.
 * @param {Array} [args.priorJudgeFindings] — when the engineer hits
 *   "Regenerate addressing N findings", these get appended to the user
 *   prompt as an "Address these previous-iteration concerns" block.
 * @param {Function} args.getApiToken — useAuth().getApiToken
 *
 * @returns {Promise<{ code: string, modelId: string, durationMs: number }>}
 */
export async function generateWxccFunction({ prompt, entityData, priorJudgeFindings }, getApiToken) {
  if (!API_BASE) {
    throw new Error('CC Utils API URL is not configured. Set REACT_APP_CCUTILS_API_BASE_URL.');
  }

  const headers = { 'Content-Type': 'application/json' };
  try {
    const token = await getApiToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch (e) {
    // Let the call go through unauthenticated; API GW will reject if needed.
    // eslint-disable-next-line no-console
    console.warn('Failed to acquire API token:', e?.message || e);
  }

  const body = { prompt };
  if (entityData && typeof entityData === 'object' && Object.keys(entityData).length > 0) {
    body.entityData = entityData;
  }
  if (Array.isArray(priorJudgeFindings) && priorJudgeFindings.length > 0) {
    body.priorJudgeFindings = priorJudgeFindings;
  }

  const res = await fetch(`${API_BASE}/ccutils/wxcc-function`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });

  let payload = null;
  try {
    payload = await res.json();
  } catch {
    // non-JSON error response; fall through
  }

  if (!res.ok) {
    const message = payload?.message || payload?.error || `HTTP ${res.status}`;
    throw new Error(`WxCC Function generator: ${message}`);
  }

  if (!payload?.code) {
    throw new Error('WxCC Function generator: response missing `code` field.');
  }

  return payload;
}
